import { PropertyResponseDto, UpdatePropertyDto } from "@/common/api/datasources/Api";
import { propertiesService } from "@/common/api/services/properties";
import { useToast } from "@/hooks/use-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export const usePropertyQuery = (propertyUUID: string) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: property, isLoading, error } = useQuery<PropertyResponseDto>({
    queryKey: ["property", propertyUUID],
    queryFn: () => propertiesService.getPropertyByUuid(propertyUUID),
  });

  const { mutate: updateProperty, isPending: isUpdating } = useMutation({
    mutationFn: (data: UpdatePropertyDto) => propertiesService.updateProperty(propertyUUID, data),
    onSuccess: () => {
      toast({
        title: t("properties.success.updated"),
      });
      queryClient.invalidateQueries({ queryKey: ["property", propertyUUID] });
    },
    onError: () => {
      toast({
        title: t("properties.errors.updateError"),
      });
    },
  });

  const { mutate: uploadImages, isPending: isUploading } = useMutation({
    mutationFn: ({ files }: { files: File[] }) => propertiesService.uploadImages(propertyUUID, files, property?.images?.length || 0),
    onSuccess: () => {
      toast({
        title: t("properties.success.imagesUploaded"),
      });
      queryClient.invalidateQueries({ queryKey: ["property", propertyUUID] });
    },
    onError: () => {
      toast({
        title: t("properties.errors.uploadError"),
        variant: "destructive",
      });
    },
  });

  const { mutate: reorderImages, isPending: isReordering } = useMutation({
    mutationFn: (imageOrders: { imageUUID: string; order: number }[]) => 
      propertiesService.reorderImages(propertyUUID, imageOrders),
    onSuccess: () => {
      toast({
        title: t("properties.success.imagesReordered"),
      });
      queryClient.invalidateQueries({ queryKey: ["property", propertyUUID] });
    },
    onError: () => {
      toast({
        title: t("properties.errors.reorderError"),
        variant: "destructive",
      });
    },
  });

  const { mutate: deleteImage, isPending: isDeleting } = useMutation({
    mutationFn: (imageUUID: string) => propertiesService.deleteImage(propertyUUID, imageUUID),
    onSuccess: () => {
      toast({
        title: t("properties.success.imageDeleted"),
      });
      queryClient.invalidateQueries({ queryKey: ["property", propertyUUID] });
    },
    onError: () => {
      toast({
        title: t("properties.errors.deleteImageError"),
        variant: "destructive",
      });
    },
  });

  const { mutate: deleteAllImages, isPending: isDeletingAll } = useMutation({
    mutationFn: () => propertiesService.deleteAllImages(propertyUUID),
    onSuccess: () => {
      toast({
        title: t("properties.success.imagesDeleted"),
      });
      queryClient.invalidateQueries({ queryKey: ["property", propertyUUID] });
    },
    onError: () => {
      toast({
        title: t("properties.errors.deleteAllImagesError"),
        variant: "destructive",
      });
    },
  });

  return {
    property,
    isLoading,
    error,
    updateProperty,
    isUpdating,
    uploadImages,
    isUploading,
    reorderImages,
    isReordering,
    deleteImage,
    isDeleting,
    deleteAllImages,
    isDeletingAll,
  };
}; 